import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRoute, useLocation } from 'wouter';
import { Helmet } from 'react-helmet-async';
import { invitationService } from '@/services/api/invitation.service';
import { apiClient } from '@/lib/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormField } from '@/components/ui/form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { PhoneInput } from '@/components/ui/phone-input';
import { invitationAcceptSchema, type InvitationAcceptFormData } from '@/schemas/invitation.schema';
import type { InvitationValidationResponse } from '@/types/invitation';
import { getErrorMessage } from '@/lib/error-utils';
import { Mail, Building2, UserCheck, AlertCircle, CheckCircle } from 'lucide-react';
import { LanguagePill } from '@/components/chrome/LanguagePill';

export default function AcceptInvitationPage() {
  const [, params] = useRoute<{ token: string }>('/accept-invitation/:token');
  const [, setLocation] = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [validation, setValidation] = useState<InvitationValidationResponse | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptSuccess, setAcceptSuccess] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const token = params?.token || '';

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<InvitationAcceptFormData>({
    resolver: zodResolver(invitationAcceptSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      password: '',
      passwordConfirmation: ''
    }
  });

  useEffect(() => {
    const validateInvitation = async () => {
      if (!token) {
        setValidation({ valid: false, message: 'No invitation token provided' });
        setIsValidating(false);
        return;
      }

      const result = await invitationService.validateInvitation(token);
      setValidation(result);

      // Pre-fill form if invitation has user info
      if (result.valid && result.invitation) {
        if (result.invitation.firstName) {
          setValue('firstName', result.invitation.firstName);
        }
        if (result.invitation.lastName) {
          setValue('lastName', result.invitation.lastName);
        }
      }

      setIsValidating(false);
    };

    validateInvitation();
  }, [token]);

  const onSubmit = async (data: InvitationAcceptFormData) => {
    setIsAccepting(true);
    setAcceptError(null);

    try {
      const response = await invitationService.acceptInvitation({
        token,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        password: validation?.userExists ? undefined : data.password
      });

      // Set auth tokens
      apiClient.setAuthTokens(response.accessToken, response.refreshToken);

      // Show success and redirect based on role
      setAcceptSuccess(true);
      setTimeout(() => {
        const dashboardPath = response.user.role === 'staff' ? '/staff/dashboard' : '/admin/dashboard';
        setLocation(dashboardPath);
      }, 2000);
    } catch (error: any) {
      console.error('Failed to accept invitation:', error);
      setAcceptError(getErrorMessage(error, 'We could not accept this invitation. Please try again.'));
    } finally {
      setIsAccepting(false);
    }
  };

  const handleExistingUserAccept = async () => {
    setIsAccepting(true);
    setAcceptError(null);

    try {
      const response = await invitationService.acceptInvitation({
        token
      });

      // Set auth tokens
      apiClient.setAuthTokens(response.accessToken, response.refreshToken);

      // Redirect based on role
      const dashboardPath = response.user.role === 'staff' ? '/staff/dashboard' : '/admin/dashboard';
      setLocation(dashboardPath);
    } catch (error: any) {
      console.error('Failed to accept invitation:', error);
      setAcceptError(getErrorMessage(error, 'We could not accept this invitation. Please try again.'));
    } finally {
      setIsAccepting(false);
    }
  };

  const password = watch('password');

  // Loading state
  if (isValidating) {
    return (
      <div className="container max-w-2xl mx-auto py-12 px-4">
        <div className="flex justify-end mb-4">
          <LanguagePill />
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Spinner className="w-8 h-8" />
              <p className="text-soft">Validating invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid invitation
  if (!validation?.valid) {
    return (
      <div className="container max-w-2xl mx-auto py-12 px-4">
        <div className="flex justify-end mb-4">
          <LanguagePill />
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              Invalid Invitation
            </CardTitle>
            <CardDescription>
              This invitation link is invalid or has expired
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>
                {validation?.message || 'The invitation may have expired, been revoked, or already been accepted.'}
              </AlertDescription>
            </Alert>

            <Button onClick={() => setLocation('/login')} className="w-full">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (acceptSuccess) {
    return (
      <div className="container max-w-2xl mx-auto py-12 px-4">
        <div className="flex justify-end mb-4">
          <LanguagePill />
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-6 py-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-h1 text-ink">Welcome Aboard!</h3>
                <p className="text-soft">
                  You have successfully joined {validation.invitation?.organizationName}
                </p>
                <p className="text-sm text-soft">
                  Redirecting to dashboard...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const invitation = validation.invitation!;

  // Existing user - just needs to accept
  if (validation.userExists) {
    return (
      <div className="container max-w-2xl mx-auto py-12 px-4">
        <div className="flex justify-end mb-4">
          <LanguagePill />
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              Accept Invitation
            </CardTitle>
            <CardDescription>
              You already have an account. Accept to join the organization.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <Building2 className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-medium">You've been invited to join:</p>
                  <p className="text-lg">{invitation.organizationName}</p>
                </div>
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label className="text-soft">Email</Label>
                <p className="font-medium">{invitation.email}</p>
              </div>
              <div className="flex justify-between items-center">
                <Label className="text-soft">Role</Label>
                <Badge>{invitation.role}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <Label className="text-soft">Invited by</Label>
                <p className="font-medium">{invitation.invitedBy}</p>
              </div>
            </div>

            <Separator />

            {acceptError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{acceptError}</AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleExistingUserAccept}
              className="w-full"
              disabled={isAccepting}
            >
              {isAccepting ? 'Accepting...' : 'Accept & Login'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // New user - needs to create account
  return (
    <div className="container max-w-2xl mx-auto py-12 px-4">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="flex justify-end mb-4">
        <LanguagePill />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Accept Invitation
          </CardTitle>
          <CardDescription>
            Create your account to join the organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="mb-6">
            <Building2 className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">You've been invited to join:</p>
                <p className="text-lg">{invitation.organizationName}</p>
                <div className="flex gap-2 mt-2">
                  <Badge>{invitation.role}</Badge>
                  <Badge variant="outline">Invited by {invitation.invitedBy}</Badge>
                </div>
              </div>
            </AlertDescription>
          </Alert>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="First Name" required error={errors.firstName?.message}>
                <Input id="firstName" placeholder="John" {...register('firstName')} />
              </FormField>
              <FormField label="Last Name" required error={errors.lastName?.message}>
                <Input id="lastName" placeholder="Doe" {...register('lastName')} />
              </FormField>
            </div>

            <FormField label="Email Address">
              <Input
                id="email"
                type="email"
                value={invitation.email}
                disabled
                className="bg-muted"
              />
            </FormField>

            <FormField label="Phone Number" optional error={errors.phone?.message}>
              <PhoneInput
                value={watch('phone') || ''}
                onChange={(value) => setValue('phone', value || '')}
              />
            </FormField>

            <Separator />

            <FormField label="Password" required error={errors.password?.message}>
              <Input
                id="password"
                type="password"
                placeholder="Create a strong password"
                {...register('password')}
              />
            </FormField>
            {password && (
              <div className="text-xs space-y-1 -mt-2">
                <p className={password.length >= 8 ? 'text-success' : 'text-soft'}>
                  ✓ At least 8 characters
                </p>
                <p className={/[A-Z]/.test(password) ? 'text-success' : 'text-soft'}>
                  ✓ One uppercase letter
                </p>
                <p className={/[a-z]/.test(password) ? 'text-success' : 'text-soft'}>
                  ✓ One lowercase letter
                </p>
                <p className={/[0-9]/.test(password) ? 'text-success' : 'text-soft'}>
                  ✓ One number
                </p>
              </div>
            )}

            <FormField label="Confirm Password" required error={errors.passwordConfirmation?.message}>
              <Input
                id="passwordConfirmation"
                type="password"
                placeholder="Confirm your password"
                {...register('passwordConfirmation')}
              />
            </FormField>

            {acceptError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{acceptError}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={isAccepting}>
              {isAccepting ? 'Creating Account...' : 'Accept Invitation & Create Account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
